import {BaseAlert} from "@/components/BaseAlert";
import {BaseButton} from "@/components/BaseButton";
import {BaseCard} from "@/components/BaseCard";
import {BaseInput} from "@/components/BaseInput";
import {CAMPAIGN_CATEGORIES, NOTIFICATION_SCREENS} from "@/constants/push.constants";
import {PATHS} from "@/constants/routes";
import {useSendToUser} from "@/hooks/usePush";
import type {CampaignCategory, SendToUserDto} from "@/types/push.types";
import {ArrowLeft, Send} from "lucide-react";
import {useState} from "react";
import {useNavigate} from "react-router";

export const SendToUser = () => {
  const navigate = useNavigate();
  const sendToUser = useSendToUser();

  const [formData, setFormData] = useState<SendToUserDto>({
    userId: "",
    title: "",
    body: "",
    category: "general",
    dataPayload: {},
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [alertState, setAlertState] = useState<{
    open: boolean;
    variant: "success" | "danger";
    title: string;
    description?: string;
  }>({open: false, variant: "success", title: ""});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.userId.trim()) {
      newErrors.userId = "User ID is required";
    }

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    } else if (formData.title.length > 200) {
      newErrors.title = "Title must be 200 characters or less";
    }

    if (!formData.body.trim()) {
      newErrors.body = "Body is required";
    } else if (formData.body.length > 1000) {
      newErrors.body = "Body must be 1000 characters or less";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      await sendToUser.mutateAsync(formData);
      setAlertState({
        open: true,
        variant: "success",
        title: "Notification sent",
        description: "Notification sent successfully!",
      });
      navigate(PATHS.PUSH_CAMPAIGNS);
    } catch {
      setAlertState({open: true, variant: "danger", title: "Error", description: "Failed to send notification"});
    }
  };

  return (
    <div className="flex flex-col gap-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <BaseButton
          variant="ghost"
          size="sm"
          onClick={() => navigate(PATHS.PUSH_CAMPAIGNS)}
          leftIcon={<ArrowLeft size={18} />}
        >
          Back
        </BaseButton>
        <div>
          <h1
            className="text-2xl font-bold"
            style={{color: "var(--color-text)"}}
          >
            Send Notification to User
          </h1>
          <p
            className="text-sm mt-1"
            style={{color: "var(--color-text-secondary)"}}
          >
            Send a one-off notification to a specific user
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <BaseCard padding="md">
          <div className="flex flex-col gap-4">
            {/* User ID */}
            <div>
              <label
                htmlFor="user-id"
                className="block text-sm font-medium mb-1.5"
                style={{color: "var(--color-text)"}}
              >
                User ID <span style={{color: "var(--color-danger)"}}>*</span>
              </label>
              <BaseInput
                id="user-id"
                type="text"
                placeholder="Enter user UUID"
                value={formData.userId}
                onChange={e => setFormData(prev => ({...prev, userId: e.target.value}))}
                error={errors.userId}
              />
              {errors.userId && (
                <p
                  className="text-xs mt-1"
                  style={{color: "var(--color-danger)"}}
                >
                  {errors.userId}
                </p>
              )}
            </div>

            {/* Title */}
            <div>
              <label
                htmlFor="title"
                className="block text-sm font-medium mb-1.5"
                style={{color: "var(--color-text)"}}
              >
                Title <span style={{color: "var(--color-danger)"}}>*</span>
              </label>
              <BaseInput
                id="title"
                type="text"
                placeholder="Notification title"
                value={formData.title}
                onChange={e => setFormData(prev => ({...prev, title: e.target.value}))}
                error={errors.title}
              />
              <p
                className="text-xs mt-1"
                style={{color: "var(--color-text-secondary)"}}
              >
                {formData.title.length}/200 characters
              </p>
              {errors.title && (
                <p
                  className="text-xs mt-1"
                  style={{color: "var(--color-danger)"}}
                >
                  {errors.title}
                </p>
              )}
            </div>

            {/* Body */}
            <div>
              <label
                htmlFor="body"
                className="block text-sm font-medium mb-1.5"
                style={{color: "var(--color-text)"}}
              >
                Body <span style={{color: "var(--color-danger)"}}>*</span>
              </label>
              <textarea
                id="body"
                rows={4}
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{
                  backgroundColor: "var(--color-bg)",
                  border: errors.body ? "1px solid var(--color-danger)" : "1px solid var(--color-surface-2)",
                  color: "var(--color-text)",
                }}
                placeholder="Notification body text"
                value={formData.body}
                onChange={e => setFormData(prev => ({...prev, body: e.target.value}))}
              />
              <p
                className="text-xs mt-1"
                style={{color: "var(--color-text-secondary)"}}
              >
                {formData.body.length}/1000 characters
              </p>
              {errors.body && (
                <p
                  className="text-xs mt-1"
                  style={{color: "var(--color-danger)"}}
                >
                  {errors.body}
                </p>
              )}
            </div>

            {/* Category */}
            <div>
              <label
                htmlFor="category"
                className="block text-sm font-medium mb-1.5"
                style={{color: "var(--color-text)"}}
              >
                Category
              </label>
              <select
                id="category"
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{
                  backgroundColor: "var(--color-bg)",
                  border: "1px solid var(--color-surface-2)",
                  color: "var(--color-text)",
                }}
                value={formData.category}
                onChange={e => setFormData(prev => ({...prev, category: e.target.value as CampaignCategory}))}
              >
                {CAMPAIGN_CATEGORIES.map(cat => (
                  <option
                    key={cat.value}
                    value={cat.value}
                  >
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Navigation Screen */}
            <div>
              <label
                htmlFor="screen"
                className="block text-sm font-medium mb-1.5"
                style={{color: "var(--color-text)"}}
              >
                Navigation Screen (Optional)
              </label>
              <select
                id="screen"
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{
                  backgroundColor: "var(--color-bg)",
                  border: "1px solid var(--color-surface-2)",
                  color: "var(--color-text)",
                }}
                value={formData.dataPayload?.screen ?? ""}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    dataPayload: {...prev.dataPayload, screen: e.target.value || undefined},
                  }))
                }
              >
                <option value="">None</option>
                {NOTIFICATION_SCREENS.map(screen => (
                  <option
                    key={screen.value}
                    value={screen.value}
                  >
                    {screen.label}
                  </option>
                ))}
              </select>
              <p
                className="text-xs mt-1"
                style={{color: "var(--color-text-secondary)"}}
              >
                Select where the user should be redirected when tapping the notification
              </p>
            </div>

            {/* Preview */}
            <div>
              <label
                className="block text-sm font-medium mb-2"
                style={{color: "var(--color-text)"}}
              >
                Preview
              </label>
              <div
                className="p-4 rounded-lg border"
                style={{
                  backgroundColor: "var(--color-surface-2)",
                  borderColor: "var(--color-surface-2)",
                }}
              >
                <p
                  className="text-sm font-semibold mb-1"
                  style={{color: "var(--color-text)"}}
                >
                  {formData.title || "Notification title"}
                </p>
                <p
                  className="text-xs"
                  style={{color: "var(--color-text-secondary)"}}
                >
                  {formData.body || "Notification body text will appear here"}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 justify-end pt-4">
              <BaseButton
                variant="ghost"
                onClick={() => navigate(PATHS.PUSH_CAMPAIGNS)}
                type="button"
              >
                Cancel
              </BaseButton>
              <BaseButton
                variant="primary"
                type="submit"
                disabled={sendToUser.isPending}
                leftIcon={<Send size={18} />}
              >
                {sendToUser.isPending ? "Sending..." : "Send Notification"}
              </BaseButton>
            </div>
          </div>
        </BaseCard>
      </form>

      <BaseAlert
        open={alertState.open}
        variant={alertState.variant}
        title={alertState.title}
        description={alertState.description}
        onClose={() => setAlertState(prev => ({...prev, open: false}))}
      />
    </div>
  );
};
